import dayjs from 'dayjs';
import { Maybe } from '../../types/types.generated';

/**
 * Gets the stored PiNxt (unencrypted) user JWT token used to pass via header
 * for SOME API requests.
 */
export const getIdToken = (): Maybe<string> => {
  return localStorage.getItem('id_token');
};

/**
 * Sets the stored PiNxt (unencrypted) user JWT token used to pass via header
 * for SOME API requests.
 * @param idToken User's idToken (unencrypted JWT)
 */
export const setIdToken = (idToken: string): void => {
  localStorage.setItem('id_token', idToken);
};

/**
 * Gets the exp time of the Anonymous token and when it must be refreshed IF the user
 * is still active on the site.
 * @returns time in Milliseconds
 */
export const getAnonymousTokenExp = (): Maybe<string> => {
  return localStorage.getItem('anonymous_token_exp');
};

/**
 * Sets the exp time of the Anonymous token and when it must be refreshed IF the user
 * is still active on the site.
 */
export const setAnonymousTokenExp = (): void => {
  localStorage.setItem(
    'anonymous_token_exp',
    // anonymous tokens have a shelf life of 1 week, setting 6 days to stay ahead of it
    dayjs().add(6, 'day').valueOf().toString()
  );
};

/**
 * Gets the stored PiNxt Anonymous token used to pass via header
 * in API requests for NON Pi auth requests
 */
export const getAnonymousToken = (): Maybe<string> => {
  return localStorage.getItem('anonymous_token') || '';
};

/**
 * Sets the stored PiNxt Anonymous token used to pass via header
 * in API requests for NON Pi auth requests
 * @param anonymousToken PiNxt issued Anonymous Token
 */
export const setAnonymousToken = (anonymousToken: string): void => {
  localStorage.setItem('anonymous_token', anonymousToken);
  setAnonymousTokenExp();
};

/**
 * Gets the exp time of the token and when it must be refreshed IF the user
 * is still active on the site.
 * @returns time in Milliseconds
 */
export const getAccessTokenExp = (): Maybe<string> => {
  return localStorage.getItem('access_token_exp');
};

/**
 * Sets the exp time of the token and when it must be refreshed IF the user
 * is still active on the site.
 */
export const setAccessTokenExp = (): void => {
  localStorage.setItem(
    'access_token_exp',
    // access tokens have a shelf life of 1 hour, setting 20 mins to stay ahead of it
    dayjs().add(20, 'minutes').valueOf().toString()
  );
};

/**
 * Gets the stored PiNxt access user (JWE) token used to pass via header
 * in API requests.
 */
export const getAccessToken = (): Maybe<string> => {
  return localStorage.getItem('access_token') || '';
};

/**
 * Sets the stored PiNxt access user (JWE) token used to pass via header
 * in API requests.
 * @param accessToken User's Access Token (JWE)
 */
export const setAccessToken = (accessToken: string): void => {
  localStorage.setItem('access_token', accessToken);
  setAccessTokenExp();
};

/**
 * Gets the stored PiNxt refresh user (JWE) token used to pass via header
 * in API requests.
 */
export const getRefreshToken = (): Maybe<string> => {
  return localStorage.getItem('refresh_token') || '';
};

/**
 * Sets the stored PiNxt refresh user (JWE) token used to pass via header
 * in API requests.
 * @param refreshToken User's Auth Token (JWE)
 */
export const setRefreshToken = (refreshToken: string): void => {
  localStorage.setItem('refresh_token', refreshToken);
};

/**
 * Clears sessionStorage and Auth Tokens in localStorage
 */
export const clearSessionStorageAndAuthTokens = (isMock: boolean): void => {
  if (isMock) {
    // Protect some mock data in browser storage
    const keepKeys: string[] = [
      /* add keys here */
    ];

    Object.keys(sessionStorage).forEach((key) => {
      if (keepKeys.includes(key) || key.startsWith('FLAG_')) {
        return;
      }

      sessionStorage.removeItem(key);
    });
  } else {
    sessionStorage.clear();
  }

  /**
   * Its important to clear out known localStorage items instead of clearing ALL as there
   * might be other non-senet related items in there such as Analytics Visit session info.
   */
  localStorage.removeItem('access_token');
  localStorage.removeItem('access_token_exp');
  localStorage.removeItem('id_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('anonymous_token');
  localStorage.removeItem('anonymous_token_exp');
};
